
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SermonFormData } from "./types";

export function useAIGeneration() {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);

  const generateSermon = async (params: {
    title?: string;
    scripture?: string;
    theme?: string;
  }): Promise<SermonFormData | null> => {
    setIsGenerating(true);
    
    try {
      toast({
        title: "Generating Sermon",
        description: "Our AI is creating your sermon plan...",
      });
      
      // For now, instead of actually calling the AI, we'll return a mock sermon
      // to avoid errors until we're ready to integrate the AI fully
      const mockSermon: SermonFormData = {
        title: params.title || "Finding Peace in God's Promises",
        scripture_reference: params.scripture || "Philippians 4:6-7",
        notes: "This is a placeholder for AI-generated sermon content. When fully implemented, this will provide detailed sermon notes based on your input.",
        planned_date: new Date().toISOString().split('T')[0],
        theme: params.theme || "Peace and Trust in God",
        target_duration: "30",
        key_points: "1. Understanding God's peace\n2. Practical steps to overcome anxiety\n3. Living in the promise of God's presence",
        sermon_type: "Expository",
        auto_generated: true,
        congregation_id: "" // Adding the missing required property
      };
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast({
        title: "Sermon Generated",
        description: "Your sermon plan template is ready!",
      });
      
      return mockSermon;
      
      /* Comment out the actual API call for now
      const { data, error } = await supabase.functions.invoke('generate-sermon', {
        body: {
          title: params.title || '',
          scripture: params.scripture || '',
          theme: params.theme || ''
        }
      });
      
      if (error) throw error;
      
      const currentDate = new Date();
      const sermonData: SermonFormData = {
        title: data.title || "AI Generated Sermon",
        scripture_reference: data.scripture_reference || "",
        notes: data.notes || "",
        planned_date: currentDate.toISOString().split('T')[0],
        theme: data.theme || "",
        target_duration: "30",
        key_points: data.key_points || "",
        sermon_type: data.sermon_type || "Expository",
        auto_generated: true,
        congregation_id: "" // Add the missing required property here too
      };
      
      return sermonData;
      */
    } catch (error: any) {
      console.error("Error in generateSermon:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate sermon",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const runAutoGeneration = () => {
    setIsGenerating(true);
    
    toast({
      title: "Auto-Generation Started",
      description: "Processing journal entries to create sermon plans...",
    });
    
    // Simulate processing time
    setTimeout(() => {
      toast({
        title: "Sermon Plans Generated",
        description: "New sermon plans have been created based on journal entries.",
      });
      setIsGenerating(false);
    }, 2000);
  };

  return {
    runAutoGeneration,
    generateSermon,
    isGenerating
  };
}
